import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, delay, of, throwError } from 'rxjs';

/** Ajustes guardados en el mock (persisten en el navegador para probar «Guardar»). */
const STORAGE_KEY = 'waiis.mock.zone-ad-settings';

const SETTINGS_URL = /\/backoffice\/zone-ad-settings(\?|$)/;

/** Usuario que firma los cambios en el mock (el contrato devuelve solo su uuid). */
const MOCK_USER_ID = '00000000-0000-4000-8000-000000000001';

/** Valores por defecto del código de trips-core (CR2 del change 20261022-120153). */
const DEFAULTS = {
  enabled: false,
  h3_resolution: 6,
  radius_km: 5,
  default_budget_cents: 5000,
  default_duration_days: 7,
  cooldown_days: 14,
  no_match_hours: 168,
  generation_every_hours: null,
  generation_times: ['00:20'],
  landing_url: 'https://waiis.com/descargar-waiis/',
  dsa_payor: null,
  dsa_beneficiary: null,
  advantage_audience_default: false,
  min_budget_cents_per_platform_day: 100,
  updated_at: null,
  updated_by: null,
};

/**
 * Responde en el navegador el contrato en borrador de los ajustes de anuncios
 * por zona (trips-core) mientras no existe el endpoint. Solo se registra con
 * `environment.zoneAdSettingsMock` (local). El resto de peticiones pasan tal cual.
 *
 * - `GET backoffice/zone-ad-settings` → `{ data }`
 * - `PUT backoffice/zone-ad-settings` (cuerpo completo) → `{ data }`, o 422 si
 *   no hay exactamente una forma de programar la generación (CR6) o si el
 *   presupuesto no llega al mínimo de Meta × 2 plataformas × días. El resto de
 *   validaciones solo las hace el backend real.
 */
@Injectable()
export class ZoneAdSettingsMockInterceptor implements HttpInterceptor {
  constructor() {
    console.info('[ZoneAdSettingsMock] Activo: ajustes de anuncios por zona simulados.');
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!SETTINGS_URL.test(req.url)) return next.handle(req);
    if (req.method === 'GET') return this.respond(this.read());
    if (req.method === 'PUT') return this.update(req.body as any);
    return next.handle(req);
  }

  private update(body: any): Observable<HttpEvent<unknown>> {
    const schedules = [body?.generation_every_hours, body?.generation_times].filter((v) => v !== null && v !== undefined);
    if (schedules.length !== 1) {
      const message = 'Hay que definir generation_every_hours o generation_times, solo una.';
      return throwError(
        () => new HttpErrorResponse({ status: 422, error: { message, errors: { generation_times: [message] } } }),
      ).pipe(delay(400));
    }
    const minimum = (body?.min_budget_cents_per_platform_day ?? 100) * 2 * (body?.default_duration_days ?? 0);
    if (body?.default_budget_cents < minimum) {
      const message = `El presupuesto tiene que ser de al menos ${(minimum / 100).toFixed(2).replace('.', ',')} € (el mínimo de Meta por plataforma y día, por dos plataformas y por los días).`;
      return throwError(
        () => new HttpErrorResponse({ status: 422, error: { message, errors: { default_budget_cents: [message] } } }),
      ).pipe(delay(400));
    }
    const settings = { ...body, updated_at: new Date().toISOString(), updated_by: MOCK_USER_ID };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Sin almacenamiento: el cambio vale solo para esta respuesta.
    }
    return this.respond(settings);
  }

  private read(): unknown {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      // Lo guardado antes de un campo nuevo se completa con su valor por defecto.
      if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch {
      // Sin almacenamiento: valores por defecto.
    }
    return DEFAULTS;
  }

  private respond(data: unknown): Observable<HttpEvent<unknown>> {
    return of(
      new HttpResponse({
        status: 200,
        body: { data },
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      }),
    ).pipe(delay(250 + Math.random() * 300));
  }
}
